import React, { useEffect, useMemo, useState } from 'react';
import { Form } from 'antd';
import { useStores } from '@/hooks/use-stores';
import { observer } from 'mobx-react';
import { useMeasurement } from '@/hooks/useMeasurement';
import { t } from 'i18next';
import {
  capitalize,
  CompressorParametersTableProps,
  compressorTypesAbbreviations,
  createRenderInputFunction,
  generateInitialValues,
  getTableColumns,
  isFormComplete,
  showError,
  validateAllFields,
  validateField,
} from './CompressorParametersTable.utils';
import { CompressorParametersContainer, StyledTableContainer } from './styled';

const CompressorParametersTable = observer(
  ({ compressorParameters, setIsSaveDisabled, activeTab, isHistory }: CompressorParametersTableProps) => {
    const [form] = Form.useForm();
    const { engineStore, devicesStore } = useStores();
    const { tunit } = useMeasurement();
    const unit = engineStore.unitMesurementSystem;
    const compressors = useMemo(() => devicesStore.compressors, [devicesStore.compressors]);

    const isLoading = engineStore.compressorsConfiguration.isLoading;

    const [activeField, setActiveField] = useState<string | null>(null);
    const [localValues, setLocalValues] = useState<Record<string, string | number | undefined>>({});
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    useEffect(() => {
      const initialValues = generateInitialValues(compressorParameters);
      form.setFieldsValue(initialValues);
      setLocalValues(initialValues);

      const { errors, hasErrors } = validateAllFields(compressorParameters, initialValues, (fieldName, value) =>
        validateField(fieldName, value, initialValues, compressorParameters),
      );
      setFieldErrors(errors);

      const formComplete = isFormComplete(compressorParameters, initialValues);

      setIsSaveDisabled(!formComplete || hasErrors);
    }, [compressorParameters, form]);

    const handleFieldChange = (changedFields: Record<string, string | number | undefined>) => {
      const [fieldName, value] = Object.entries(changedFields)[0];
      if (fieldName !== activeField) {
        return;
      }

      const [key, field] = fieldName.split('_');
      const parsedValue = value === '' ? undefined : value;

      setLocalValues(prev => ({
        ...prev,
        [fieldName]: value,
      }));

      let numericValue: number | string | undefined;
      if (['stopDelayMinutes', 'priority'].includes(field)) {
        numericValue = typeof parsedValue === 'string' ? parseInt(parsedValue, 10) : undefined;
      }

      if (['maxTarget', 'minTarget'].includes(field)) {
        numericValue = parsedValue;
      }

      engineStore.compressorsConfiguration.setCompressorParameterField(key, field, numericValue ?? parsedValue);

      const errorMessage = validateField(fieldName, value, localValues, compressorParameters);
      if (errorMessage) {
        setIsSaveDisabled(true);
      }
    };

    const onInputFocus = (fieldName: string) => {
      setActiveField(fieldName);
    };

    const onInputBlur = () => {
      if (activeField) {
        const errorMessage = validateField(activeField, localValues[activeField], localValues, compressorParameters);
        if (errorMessage) {
          setFieldErrors(prev => ({
            ...prev,
            [activeField]: errorMessage,
          }));

          showError(errorMessage, setIsSaveDisabled);
        } else {
          setFieldErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[activeField];
            return newErrors;
          });
        }

        const formComplete = isFormComplete(compressorParameters, localValues);
        const hasErrors = Object.keys(fieldErrors).length > 0;
        setIsSaveDisabled(!formComplete || hasErrors);
      }

      setActiveField(null);
    };

    const onInputScroll = (e: React.WheelEvent) => (e.target as HTMLInputElement)?.blur();

    const renderInput = createRenderInputFunction(fieldErrors, onInputScroll, onInputFocus, onInputBlur, activeTab);

    const columns = getTableColumns(
      compressors,
      compressorTypesAbbreviations,
      renderInput,
      tunit,
      unit,
      isLoading,
      isHistory,
    );

    const sortedCompressorParameters = useMemo(() => {
      if (!compressorParameters) {
        return [];
      }
      return [...compressorParameters].sort((a, b) => {
        const priorityA = a.priority !== undefined ? Number(a.priority) : Infinity;
        const priorityB = b.priority !== undefined ? Number(b.priority) : Infinity;
        return priorityA - priorityB;
      });
    }, [compressorParameters]);

    const getDataSource = () => {
      if (isLoading) {
        const emptyDataCount = compressorParameters?.length || 1;
        return Array(emptyDataCount)
          .fill(null)
          .map((_, index) => ({
            key: String(index + 1),
            compressorType: 'Compressor Type 1',
            compressorName: 'Compressor 1',
            maxTarget: 100,
            minTarget: 50,
            stopDelayMinutes: 10,
            priority: 1,
          }));
      }
      return sortedCompressorParameters;
    };

    return (
      <CompressorParametersContainer>
        <h4 className="unit-measurement-header">
          {t('Measurement system')}: {capitalize(unit)}
        </h4>
        <Form form={form} onValuesChange={handleFieldChange}>
          <StyledTableContainer columns={columns} dataSource={getDataSource()} pagination={false} />
        </Form>
      </CompressorParametersContainer>
    );
  },
);

export default CompressorParametersTable;
